import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useOutletContext, Link } from 'react-router-dom';
import {
  Send, Bell, Pin, Trash2, MessageSquare, Zap, Reply, X, ExternalLink, Smile, Megaphone,
  FileText, Download, Plus,
} from 'lucide-react';
import api from '../services/api.js';
import { chatSocket } from '../services/socket.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useTeamRole } from '../hooks/useTeamRole.js';
import toast from 'react-hot-toast';
import { format, isToday, isYesterday, differenceInMinutes } from 'date-fns';
import Avatar from '../components/shared/Avatar.jsx';
import EmojiPicker from '../components/shared/EmojiPicker.jsx';

// ── URL detection & safe link rendering ─────────────────────────────────────
// Matches http(s) URLs and bare www. URLs. Splits the string so URL tokens
// get rendered as clickable anchors while preserving surrounding text.
const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

function hostFromUrl(url) {
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, '');
  } catch { return url; }
}

function renderMessageContent(text) {
  if (!text) return null;
  const parts = text.split(URL_REGEX);
  return parts.map((part, i) => {
    if (!part) return null;
    if (URL_REGEX.test(part)) {
      URL_REGEX.lastIndex = 0;
      const href = part.startsWith('http') ? part : `https://${part}`;
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            color: 'inherit', textDecoration: 'underline',
            textDecorationColor: 'currentColor', textUnderlineOffset: 2,
            wordBreak: 'break-all',
          }}>
          {part}
        </a>
      );
    }
    URL_REGEX.lastIndex = 0;
    return <span key={i}>{part}</span>;
  });
}

// Extract the first URL from text so we can render a simple link preview card
function firstUrl(text) {
  if (!text) return null;
  URL_REGEX.lastIndex = 0;
  const match = text.match(URL_REGEX);
  if (!match) return null;
  const url = match[0];
  return url.startsWith('http') ? url : `https://${url}`;
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function ChatPage() {
  const { teamId } = useParams();
  const { currentSeasonId = null } = useOutletContext() || {}; // v2.5.189
  const { user }   = useAuth();
  const { isCoach } = useTeamRole(teamId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isAnnouncement, setIsAnnouncement] = useState(false);
  // Phase B (league gap-closure, 2026-06-09) — entry point to the
  // league coaches' channel for coaches whose team is enrolled in a
  // league. Coaches usually are NOT league_members, so the league
  // sidebar never shows for them; this pill is how they reach the
  // cross-team channel. Probe order: the team-side announcements
  // endpoint (cheap, no feature-flag gate) tells us the league_id;
  // only then do we hit the chat context probe (which IS flag-gated
  // — enrolled coaches got the flag at invite acceptance). Both
  // failures are silent: no league, no pill.
  const [leagueChat, setLeagueChat] = useState(null); // { leagueId } when available
  useEffect(() => {
    if (!isCoach || !teamId) { setLeagueChat(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data: ann } = await api.get(`/teams/${teamId}/league-announcements?limit=1`);
        const leagueId = ann?.league_id;
        if (!leagueId || cancelled) return;
        const { data: ctx } = await api.get(`/leagues/${leagueId}/chat/context`);
        if (cancelled) return;
        if (ctx?.can_access) {
          // chatEnabled gates the League-chat pill; the Team-news
          // pill (public-site authoring, P4) shows for any enrolled
          // coach regardless of the chat toggle.
          setLeagueChat({ leagueId, chatEnabled: ctx?.coaches_channel_enabled === true });
        }
      } catch {
        /* silent — most teams aren't in a league */
      }
    })();
    return () => { cancelled = true; };
  }, [isCoach, teamId]);
  // May 4 2026 (v2.5.70) — non-coach recipient scope. Coach feedback:
  // "on the player side of things, for the chat, give two buttons for
  // the player, either, the message goes to the coaches only, or to
  // the entire team. By default, have the message go to the entire
  // team." Default 'everyone' matches the coach's spec. Field is
  // ignored entirely for coaches (they have the Announce button +
  // team-level chat_notify_mode for scope). The composer below only
  // renders the two-button picker when !isCoach. ── */}
  const [playerRecipientScope, setPlayerRecipientScope] = useState('everyone');
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [activeMsgId, setActiveMsgId] = useState(null);
  const [showPinnedExpanded, setShowPinnedExpanded] = useState(false);
  // Reaction picker: which message is currently showing its emoji picker.
  // null = no picker open. Separate from activeMsgId so tapping a bubble
  // shows actions (reply/pin/delete) first; tapping the smile icon opens
  // the emoji picker for that specific message.
  const [reactionPickerFor, setReactionPickerFor] = useState(null);

  const REACTION_EMOJI = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

  // Apr 2026 — composer emoji picker. Opens above the input when the
  // smile button next to send is tapped. Picks insert at the current
  // cursor position; doesn't auto-close so you can grab a few in a
  // row.
  const [composerEmojiOpen, setComposerEmojiOpen] = useState(false);

  // Apr 2026 — chat attachments. `pendingFile` is the File object the
  // user has staged (from picker or drag-drop) but not yet sent.
  // Showing a preview in the composer area lets them confirm the file
  // before committing the upload, and clear/replace it without
  // dispatching a send. `uploading` flips while the multipart POST is
  // in flight.
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const fileInputRef = useRef(null);
  const [dragOver,    setDragOver]    = useState(false);
  // Per-team chat config (feature flag + cap). Loaded once at mount.
  // Falls back to {attachmentsEnabled: false} on error so the
  // + button stays hidden if the probe fails — safe default.
  const [chatConfig, setChatConfig] = useState({ attachmentsEnabled: false });

  // Memoize the staged-file preview object URL so we don't allocate
  // a fresh blob URL on every re-render. useEffect cleanup revokes
  // the URL when the user clears the file, picks a different one,
  // or unmounts — that's the only correct cleanup point (a previous
  // version revoked onLoad which leaked URLs whenever the image
  // failed to load).
  const pendingFilePreviewUrl = useMemo(() => {
    if (!pendingFile || !pendingFile.type.startsWith('image/')) return null;
    return URL.createObjectURL(pendingFile);
  }, [pendingFile]);
  useEffect(() => {
    return () => {
      if (pendingFilePreviewUrl) URL.revokeObjectURL(pendingFilePreviewUrl);
    };
  }, [pendingFilePreviewUrl]);

  const bottomRef = useRef(null);
  const typingTimer = useRef(null);
  const textareaRef = useRef(null);
  const scrollRef = useRef(null);
  // Tracks whether the user is currently scrolled near the bottom. If so,
  // new messages auto-scroll into view. If they've scrolled up to read older
  // content, a new message won't yank them away.
  const nearBottomRef = useRef(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/teams/${teamId}/chat`, { params: currentSeasonId ? { seasonId: currentSeasonId } : {} });
        if (mounted) setMessages(data);
      } catch { if (mounted) toast.error('Failed to load messages'); }
      if (mounted) setLoading(false);
    };
    load();

    // Apr 2026 — probe chat feature config (attachments enabled,
    // per-team cap, retention). Non-blocking; defaults already in
    // state mean the rest of the chat works regardless of whether
    // this comes back.
    (async () => {
      try {
        const { data: cfg } = await api.get(`/teams/${teamId}/chat/config`);
        if (mounted) setChatConfig(cfg);
      } catch {
        // Leave defaults — + button stays hidden, which is the safe fallback.
      }
    })();

    chatSocket.joinTeam(teamId);

    const u1 = chatSocket.on('NEW_MESSAGE', (msg) =>
      // Dedup — the sender's own message is already stitched into state by
      // sendMessage(). If the broadcast reaches us anyway (reconnect race
      // condition, or future removal of the sender-exclude behaviour) we
      // don't want to show the same message twice.
      setMessages(p => {
        if (currentSeasonId && msg.season_id && msg.season_id !== currentSeasonId) return p; // v2.5.189
        return p.some(m => m.id === msg.id) ? p : [...p, msg];
      })
    );
    const u2 = chatSocket.on('MESSAGE_DELETED', ({ messageId }) =>
      setMessages(p => p.filter(m => m.id !== messageId)));
    const u3 = chatSocket.on('ONLINE_USERS', setOnlineUsers);
    const u4 = chatSocket.on('USER_ONLINE', (d) =>
      setOnlineUsers(p => [...p.filter(u => u.userId !== d.userId), d]));
    const u5 = chatSocket.on('USER_OFFLINE', ({ userId }) =>
      setOnlineUsers(p => p.filter(u => u.userId !== userId)));
    const u6 = chatSocket.on('USER_TYPING', ({ userId, userName, isTyping }) =>
      setTypingUsers(p => isTyping
        ? [...p.filter(u => u.userId !== userId), { userId, userName }]
        : p.filter(u => u.userId !== userId)));
    const u7 = chatSocket.on('REACTION_UPDATED', ({ messageId, reactions }) =>
      setMessages(p => p.map(m => m.id === messageId ? { ...m, reactions } : m)));
    // v2.5.181 — coach cleared the whole chat: empty the list live so
    // open clients don't keep showing stale (now-deleted) messages.
    const u8 = chatSocket.on('CHAT_CLEARED', () => setMessages([]));

    return () => { mounted = false; clearTimeout(typingTimer.current); u1(); u2(); u3(); u4(); u5(); u6(); u7(); u8(); };
  }, [teamId, currentSeasonId]);

  // Initial load: jump straight to the bottom (no smooth scroll) once
  // the first batch of messages has actually rendered. Previously this
  // fired only on `loading` flipping false, but the message DOM hadn't
  // painted yet at that instant, so scrollHeight was stale and the view
  // landed mid-history instead of at the newest message. We now key the
  // effect on the message list being populated AND defer the scroll to
  // after paint via requestAnimationFrame (double-rAF to be safe across
  // browsers), and only do it ONCE per chat open (didInitialScrollRef).
  const didInitialScrollRef = useRef(false);
  const initialScrollRafRef = useRef({ outer: null, inner: null });
  useEffect(() => {
    if (loading || messages.length === 0 || didInitialScrollRef.current) return;
    if (!scrollRef.current) return;
    const jump = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        nearBottomRef.current = true;
      }
    };
    // Two rAFs: first lets React commit the message DOM, second runs
    // after the browser has laid it out so scrollHeight is final.
    initialScrollRafRef.current.outer = requestAnimationFrame(() => {
      initialScrollRafRef.current.inner = requestAnimationFrame(jump);
    });
    didInitialScrollRef.current = true;
    return () => {
      cancelAnimationFrame(initialScrollRafRef.current.outer);
      cancelAnimationFrame(initialScrollRafRef.current.inner);
    };
  }, [loading, messages.length]);

  // Reset the initial-scroll guard when switching teams, so opening a
  // different team's chat scrolls to its bottom too. We also clear the
  // message list so the [loading, messages.length] scroll effect always
  // sees a 0 → N transition (otherwise switching to a team with the
  // SAME message count wouldn't re-trigger the jump).
  useEffect(() => {
    didInitialScrollRef.current = false;
    setMessages([]);
  }, [teamId]);

  useEffect(() => {
    // Only auto-scroll if user is already near the bottom — respects readers
    // who've scrolled up to review history. Skip until the initial
    // instant-jump-to-bottom has run, so we don't fire a smooth scroll
    // against a not-yet-laid-out list on first load (the instant jump
    // owns the initial position).
    if (!didInitialScrollRef.current) return;
    if (nearBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, typingUsers]);

  const handleScroll = useCallback((e) => {
    const el = e.currentTarget;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    nearBottomRef.current = distance < 120;
  }, []);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, [input]);

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const trimmed = input.trim();
    // Apr 2026 — fork early into the upload path when a file is
    // staged. Caption (input text) is optional in that case — many
    // users just attach + send. Disallow uploads from announcement
    // mode to keep semantics clean (an announcement IS the message;
    // a file IS the message; combining the two would need design).
    if (pendingFile) {
      if (sending || uploading) return;
      if (isAnnouncement) {
        toast.error('Turn off announcement mode to send a file');
        return;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', pendingFile);
        if (trimmed) fd.append('content', trimmed);
        // Override the api instance's default `Content-Type:
        // application/json` for this multipart request. We pass
        // `undefined` (not '') so axios DELETES the header from the
        // request — which lets the underlying XHR set the proper
        // `multipart/form-data; boundary=...` header itself when it
        // sees a FormData body. Setting an empty string would
        // produce `Content-Type: ` which breaks the request.
        const { data: sentMsg } = await api.post(
          `/teams/${teamId}/chat/attachments`,
          fd,
          { headers: { 'Content-Type': undefined } }
        );
        if (sentMsg?.id) {
          setMessages(prev =>
            prev.some(m => m.id === sentMsg.id) ? prev : [...prev, sentMsg]
          );
        }
        setInput('');
        setPendingFile(null);
        setReplyTo(null);
        nearBottomRef.current = true;
      } catch (err) {
        toast.error(err.response?.data?.error || 'Upload failed');
      }
      setUploading(false);
      return;
    }
    if (!trimmed || sending) return;
    setSending(true);
    chatSocket.sendTyping(false);
    clearTimeout(typingTimer.current);
    try {
      // Stitch the server's returned message into local state so the sender
      // sees their own bubble appear immediately — without this the user had
      // to refresh to see what they sent, because the backend broadcast
      // intentionally excludes the sender (to avoid double-delivery of a
      // message the sender already has "in hand"). Guard against inserting
      // a duplicate if the broadcast ever does reach the sender (e.g. after
      // a reconnect) by checking the message id isn't already in state.
      const { data: sentMsg } = await api.post(`/teams/${teamId}/chat`, {
        content: trimmed,
        messageType: isAnnouncement ? 'announcement' : 'text',
        sendEmailNotification: isAnnouncement,
        replyTo: replyTo?.id || null,
        ...(currentSeasonId ? { seasonId: currentSeasonId } : {}), // v2.5.189
        // May 4 2026 (v2.5.70) — non-coach recipient scope from the
        // player picker. v2.5.185 — ALSO send it for a coach when they
        // are replying to a coaches-only message, so the reply inherits
        // the private scope instead of falling through to the team
        // default (which broadcast the reply to everyone — the reported
        // bug). Announcements still force 'everyone' on the backend, so
        // we never send a scope for those.
        ...(() => {
          if (isAnnouncement) return {};
          if (!isCoach) return { recipientScope: playerRecipientScope };
          // Coach: only override when replying to a coaches-only message
          // (or when the player-style picker has been set to coaches —
          // harmless if the coach UI doesn't expose it). Otherwise leave
          // undefined so the team-level chat_notify_mode default applies.
          if (replyTo?.recipientScope === 'coaches' || playerRecipientScope === 'coaches') {
            return { recipientScope: 'coaches' };
          }
          return {};
        })(),
      });
      if (sentMsg && sentMsg.id) {
        setMessages(prev =>
          prev.some(m => m.id === sentMsg.id) ? prev : [...prev, sentMsg]
        );
      }
      setInput('');
      setIsAnnouncement(false);
      setReplyTo(null);
      // May 4 2026 (v2.5.70) — reset scope to the default 'everyone'
      // after every successful send. The "coaches only" choice is a
      // per-message decision (e.g. one-off question for the coach);
      // sticky behaviour would risk a player accidentally sending
      // their next group-chat message to coaches only.
      setPlayerRecipientScope('everyone');
      // Sending jumps user back to the bottom — they want to see their own
      // message land.
      nearBottomRef.current = true;
    } catch { toast.error('Failed to send'); }
    setSending(false);
  };

  // Apr 2026 — file picker / drag-drop helpers. Both paths funnel
  // through stageFile so validation lives in one place.
  const ATTACH_MAX_BYTES = 10 * 1024 * 1024;
  const ATTACH_MIME_OK = new Set([
    'image/png', 'image/jpeg', 'image/gif', 'image/webp',
    'application/pdf',
  ]);
  const stageFile = (file) => {
    if (!file) return;
    if (!chatConfig.attachmentsEnabled) {
      toast.error('File attachments are currently disabled by the administrator.');
      return;
    }
    if (!ATTACH_MIME_OK.has(file.type)) {
      toast.error('Only PNG, JPEG, GIF, WebP, or PDF files are allowed.');
      return;
    }
    if (file.size > ATTACH_MAX_BYTES) {
      toast.error(`File too large (max ${Math.round(ATTACH_MAX_BYTES / 1024 / 1024)} MB)`);
      return;
    }
    setPendingFile(file);
  };
  const handleFilePick = (e) => {
    const f = e.target.files?.[0];
    if (f) stageFile(f);
    // Reset the input so picking the same file twice in a row still fires onChange.
    e.target.value = '';
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) stageFile(f);
  };
  const handleDragOver = (e) => {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      setDragOver(true);
    }
  };
  const handleDragLeave = () => setDragOver(false);

  const handleInput = (e) => {
    setInput(e.target.value);
    chatSocket.sendTyping(true);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => chatSocket.sendTyping(false), 2000);
  };

  // Apr 2026 — insert an emoji at the textarea's cursor position. We
  // can't just append because if the user has the cursor in the
  // middle of typed text, appending feels broken. Reads
  // selectionStart/End, splices, then restores focus + sets cursor
  // right after the inserted glyph (handles surrogate pairs by using
  // String#length on the emoji, which is correct for the actual
  // string position).
  const insertEmoji = useCallback((emoji) => {
    const ta = textareaRef.current;
    const cur = input;
    if (!ta) {
      setInput(cur + emoji);
      return;
    }
    const start = ta.selectionStart ?? cur.length;
    const end   = ta.selectionEnd   ?? cur.length;
    const next  = cur.slice(0, start) + emoji + cur.slice(end);
    setInput(next);
    // Defer cursor restore to next tick so React's controlled-value
    // update has flushed first.
    setTimeout(() => {
      try {
        ta.focus();
        const pos = start + emoji.length;
        ta.setSelectionRange(pos, pos);
      } catch {}
    }, 0);
  }, [input]);

  const deleteMsg = useCallback(async (id) => {
    setActiveMsgId(null);
    try {
      await api.delete(`/teams/${teamId}/chat/${id}`);
    } catch { toast.error('Cannot delete this message'); }
  }, [teamId]);

  const togglePin = useCallback(async (id) => {
    setActiveMsgId(null);
    try {
      const { data } = await api.put(`/teams/${teamId}/chat/${id}/pin`);
      setMessages(prev => prev.map(m => m.id === id ? { ...m, isPinned: data.isPinned } : m));
      toast.success(data.isPinned ? '📌 Pinned' : 'Unpinned');
    } catch { toast.error('Failed to pin'); }
  }, [teamId]);

  const startReply = useCallback((msg) => {
    setReplyTo(msg);
    // v2.5.185 — inherit the replied-to message's scope so a reply to a
    // coaches-only note stays coaches-only. Previously a coach replying
    // to a player's private (coaches-only) message broadcast it to the
    // whole team, because the reply defaulted to 'everyone'. This sets
    // the composer's scope to match the message being answered; the
    // sender can still flip it manually before sending if they really
    // want to widen it.
    if (msg?.recipientScope === 'coaches') {
      setPlayerRecipientScope('coaches');
    }
    setActiveMsgId(null);
    setReactionPickerFor(null);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, []);

  // Toggle a reaction on the given message. Optimistic update — flips local
  // state immediately then reconciles with server response. The server also
  // broadcasts REACTION_UPDATED so other viewers get the new state.
  const toggleReaction = useCallback(async (msg, emoji) => {
    setReactionPickerFor(null);
    setActiveMsgId(null);
    // Optimistic update
    setMessages(prev => prev.map(m => {
      if (m.id !== msg.id) return m;
      const existing = (m.reactions || []).find(r => r.emoji === emoji);
      let nextRx;
      if (existing) {
        const hasMe = existing.userIds.includes(user?.id);
        const nextUserIds = hasMe
          ? existing.userIds.filter(u => u !== user?.id)
          : [...existing.userIds, user?.id];
        nextRx = nextUserIds.length === 0
          ? (m.reactions || []).filter(r => r.emoji !== emoji)
          : (m.reactions || []).map(r => r.emoji === emoji ? { ...r, userIds: nextUserIds } : r);
      } else {
        nextRx = [...(m.reactions || []), { emoji, userIds: [user?.id] }];
      }
      return { ...m, reactions: nextRx };
    }));
    try {
      const { data } = await api.post(`/teams/${teamId}/chat/${msg.id}/react`, { emoji });
      // Reconcile with server (in case of race conditions)
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, reactions: data.reactions } : m));
    } catch {
      toast.error('Failed to react');
    }
  }, [teamId, user?.id]);

  const messagesById = useMemo(() => {
    const map = new Map();
    for (const m of messages) map.set(m.id, m);
    return map;
  }, [messages]);

  const pinnedMessages = useMemo(() => messages.filter(m => m.isPinned), [messages]);

  const grouped = useMemo(() => messages.reduce((acc, msg) => {
    const k = format(new Date(msg.createdAt), 'yyyy-MM-dd');
    if (!acc[k]) acc[k] = [];
    acc[k].push(msg);
    return acc;
  }, {}), [messages]);

  const dateLabel = (str) => {
    const d = new Date(str);
    if (isToday(d)) return 'Today';
    if (isYesterday(d)) return 'Yesterday';
    return format(d, 'MMMM d, yyyy');
  };

  return (
    <div className="flex flex-col animate-fade-in"
      style={{
        // Apr 2026 — was `100dvh - 100px` (pre-BottomNav). Mobile
        // layout now has a persistent BottomNav (~54px) below the
        // scroll container, so we subtract more to keep the chat
        // composer clear of the tab bar. Formula:
        //   top bar (52) + BottomNav (54) + safe-area-inset
        //   + small buffer (10) ≈ 116 + env(...)
        // Desktop has no --mobile-h set; the fallback 100dvh is used
        // and the safe-area-inset evaluates to 0 (no home indicator
        // on desktop), so the desktop chat just gets a slightly more
        // generous bottom reserve than before. Harmless.
        height: 'calc(var(--mobile-h, 100dvh) - 116px - env(safe-area-inset-bottom, 0px))',
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      {/* Mobile fix — a coach whose team is enrolled in a league can see
          up to 3 pills here (Team news, League chat, Announce) alongside
          the title block. With no wrap this row could overflow a ~390px
          screen and clip/force horizontal scroll. flexWrap only kicks in
          when content doesn't fit on one line, so desktop (where it
          always fits) is visually unchanged; rowGap adds breathing room
          between wrapped rows. */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0" style={{ flexWrap: 'wrap', rowGap: 8 }}>
        <div className="min-w-0">
          <h1 className="section-title text-2xl">Team Chat</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <div className="w-2 h-2 rounded-full animate-pulse flex-shrink-0" style={{ background: 'var(--goal)' }} />
            <span className="text-xs text-secondary truncate">
              {onlineUsers.length > 0
                ? `${onlineUsers.length} online · ${onlineUsers.slice(0,3).map(u => u.userName?.split(' ')[0]).join(', ')}${onlineUsers.length > 3 ? '…' : ''}`
                : 'Nobody online right now'}
            </span>
          </div>
        </div>
        {/* Phase B — league coaches' channel entry point (see the
            probe effect above). Renders only when the team is in a
            league whose coaches channel is enabled and this user can
            access it. */}
        {/* P4 (public pages overhaul) — coaches post updates (with
            pictures) to their team's PUBLIC page from here. */}
        {leagueChat && (
          <Link
            to={`/league/${leagueChat.leagueId}/public-site`}
            title="Post news to your team's public page"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold flex-shrink-0 mr-2"
            style={{
              background: 'rgba(0,212,255,0.08)',
              border: '1px solid rgba(0,212,255,0.25)',
              color: 'var(--ice)',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontSize: 11,
              textDecoration: 'none',
            }}>
            <Megaphone size={13} strokeWidth={2.2} />
            Team news
          </Link>
        )}
        {leagueChat?.chatEnabled && (
          <Link
            to={`/league/${leagueChat.leagueId}/chat`}
            title="Open the league's cross-team coaches channel"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold flex-shrink-0 mr-2"
            style={{
              background: 'rgba(178,132,255,0.10)',
              border: '1px solid rgba(178,132,255,0.30)',
              color: '#b284ff',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontSize: 11,
              textDecoration: 'none',
            }}>
            <MessageSquare size={13} strokeWidth={2.2} />
            League chat
          </Link>
        )}
        {isCoach && (
          <button
            onClick={() => setIsAnnouncement(a => !a)}
            title={isAnnouncement
              ? 'Tap to switch off announcement mode'
              : 'Announcement mode — your message gets a 📢 banner and emails every team member'}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex-shrink-0"
            style={{
              // Apr 2026 — visual distinction made stronger. Off-state
              // now has the puck-orange tint at low opacity (so the
              // button is always discoverable as the announcement
              // tool, not just a generic toggle), and on-state is a
              // bold filled pill so there's no question what mode
              // the coach is in. Megaphone icon is more semantically
              // obvious than the generic Bell.
              background: isAnnouncement
                ? 'var(--puck)'
                : 'rgba(255,149,0,0.10)',
              border: `1px solid ${isAnnouncement ? 'var(--puck)' : 'rgba(255,149,0,0.30)'}`,
              color: isAnnouncement ? '#000' : 'var(--puck)',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontSize: 11,
            }}>
            <Megaphone size={13} strokeWidth={2.2} />
            {isAnnouncement ? 'Announcing' : 'Announce'}
          </button>
        )}
      </div>

      {/* Pinned messages banner — collapsible */}
      {pinnedMessages.length > 0 && (
        <div className="flex-shrink-0 mb-2 rounded-xl overflow-hidden"
          style={{ border: '1px solid rgba(255,149,0,0.2)', background: 'rgba(255,149,0,0.05)' }}>
          <button
            onClick={() => setShowPinnedExpanded(p => !p)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left">
            <Pin size={12} style={{ color: 'var(--puck)' }} />
            <span className="text-xs font-semibold" style={{ color: 'var(--puck)' }}>
              {pinnedMessages.length} pinned
            </span>
            <span className="text-xs text-secondary ml-1 flex-1 truncate">
              {!showPinnedExpanded && `"${pinnedMessages[pinnedMessages.length - 1].content?.slice(0, 60)}${pinnedMessages[pinnedMessages.length - 1].content?.length > 60 ? '…' : ''}"`}
            </span>
            <span className="text-xs text-secondary flex-shrink-0">{showPinnedExpanded ? '▲' : '▼'}</span>
          </button>
          {showPinnedExpanded && (
            <div className="px-3 pb-2 space-y-1.5">
              {pinnedMessages.map(m => (
                <div key={m.id} className="flex items-start gap-2 text-sm">
                  <span className="text-xs font-semibold flex-shrink-0" style={{ color: 'var(--puck)' }}>
                    {m.senderName?.split(' ')[0]}:
                  </span>
                  <span className="flex-1">{m.content}</span>
                  {isCoach && (
                    <button onClick={() => togglePin(m.id)}
                      className="text-xs flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>
                      Unpin
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages area — iMessage/WhatsApp-style bubbles over a subtle
          sporty pattern background (pucks + sticks tiled at low opacity).
          The SVG is inlined as a data URI so there's no network fetch and
          the opacity is low enough to not compete with message text. */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto min-h-0 chat-backdrop"
        style={{
          padding: '8px 4px 12px',
          // Layered background: top is the tiled SVG pattern (pucks + sticks
          // at 8% alpha), bottom is the page surface. Uses backgroundBlendMode
          // so the pattern inherits the user's theme color subtly.
          backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'>
  <g fill='none' stroke='#9ca3af' stroke-width='1.4' stroke-linecap='round' stroke-linejoin='round' opacity='0.18'>
    <ellipse cx='22' cy='22' rx='10' ry='3.2'/>
    <path d='M12 22 L12 26 A10 3.2 0 0 0 32 26 L32 22'/>
    <path d='M78 78 L102 54 M78 78 L74 86 L92 86 Z'/>
    <ellipse cx='70' cy='92' rx='8' ry='2.6'/>
    <path d='M62 92 L62 95 A8 2.6 0 0 0 78 95 L78 92'/>
    <path d='M48 40 L52 44 L48 48'/>
    <path d='M20 88 L24 92 L20 96'/>
  </g>
</svg>`)}")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '120px 120px',
          backgroundColor: 'var(--surface)',
        }}
        onClick={() => { setActiveMsgId(null); setReactionPickerFor(null); }}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="spinner" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-secondary px-8 text-center">
            <MessageSquare size={44} className="mb-3 opacity-20" />
            <p className="font-semibold">No messages yet</p>
            <p className="text-sm mt-1 opacity-70">Break the ice — say hi to your team!</p>
          </div>
        ) : (
          <>
            {Object.entries(grouped).map(([dateKey, dayMsgs]) => (
              <div key={dateKey}>
                {/* Date separator — centered pill that floats over the
                    patterned backdrop with a subtle blur for a clean read */}
                <div className="flex items-center justify-center my-4">
                  <span className="px-3 py-1 rounded-full font-semibold"
                    style={{
                      background: 'rgba(255,255,255,0.72)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      color: 'var(--text-secondary)',
                      fontSize: 11,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    }}>
                    {dateLabel(dayMsgs[0].createdAt)}
                  </span>
                </div>

                {dayMsgs.map((msg, i) => {
                  const isMe = msg.senderId === user?.id;
                  const prev = i > 0 ? dayMsgs[i - 1] : null;
                  const next = i < dayMsgs.length - 1 ? dayMsgs[i + 1] : null;

                  // Timestamp separator — iMessage-style centered time label
                  // when a message is separated from the previous one by more
                  // than 15 minutes. Helps the reader see conversation gaps.
                  const showTimeBreak = prev
                    && differenceInMinutes(new Date(msg.createdAt), new Date(prev.createdAt)) >= 15;
                  const timeBreakLabel = showTimeBreak
                    ? new Date(msg.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                    : null;

                  // Cluster logic: same sender within 5 min of neighbor = tight
                  const sameSenderAsPrev = prev && prev.senderId === msg.senderId &&
                    differenceInMinutes(new Date(msg.createdAt), new Date(prev.createdAt)) < 5 &&
                    prev.messageType !== 'announcement' && prev.messageType !== 'ai';
                  const sameSenderAsNext = next && next.senderId === msg.senderId &&
                    differenceInMinutes(new Date(next.createdAt), new Date(msg.createdAt)) < 5 &&
                    next.messageType !== 'announcement' && next.messageType !== 'ai';

                  const isAnnounce = msg.messageType === 'announcement';
                  const isAI       = msg.messageType === 'ai';

                  if (isAnnounce) return (
                    <div key={msg.id} className="my-4 rounded-xl px-4 py-3 text-center animate-puck-in"
                      style={{ background: 'rgba(255,149,0,0.09)', border: '1px solid rgba(255,149,0,0.25)' }}>
                      <div className="flex items-center justify-center gap-2 mb-1 text-xs font-bold" style={{ color: 'var(--puck)' }}>
                        <Bell size={12} /> Announcement from {msg.senderName}
                      </div>
                      <p className="text-sm" style={{ lineHeight: 1.5 }}>{renderMessageContent(msg.content)}</p>
                      <div className="text-xs text-secondary mt-1.5">
                        {format(new Date(msg.createdAt), 'h:mm a')} · Emailed to team
                      </div>
                    </div>
                  );

                  if (isAI) return (
                    <div key={msg.id} className="my-4 rounded-xl px-4 py-3 animate-puck-in"
                      style={{ background: 'var(--ice-a7)', border: '1px solid var(--ice-a15)' }}>
                      <div className="flex items-center gap-2 mb-1 text-xs font-bold" style={{ color: 'var(--ice)' }}>
                        <Zap size={12} /> AI Assistant
                      </div>
                      <p className="text-sm" style={{ lineHeight: 1.5 }}>{renderMessageContent(msg.content)}</p>
                    </div>
                  );

                  // ── Regular text message bubble ────────────────────────────
                  const linkUrl = firstUrl(msg.content);
                  const repliedTo = msg.replyTo ? messagesById.get(msg.replyTo) : null;
                  const isActive = activeMsgId === msg.id;
                  const canAct = isMe || isCoach;

                  // iMessage-style cluster corners — inner corners shrink when
                  // the previous/next message is from the same sender.
                  const bubbleRadius = isMe
                    ? `18px ${sameSenderAsPrev ? '6px' : '18px'} ${sameSenderAsNext ? '6px' : '18px'} 18px`
                    : `${sameSenderAsPrev ? '6px' : '18px'} 18px 18px ${sameSenderAsNext ? '6px' : '18px'}`;

                  return (
                    <div key={msg.id}>
                      {timeBreakLabel && (
                        <div style={{
                          display: 'flex', justifyContent: 'center',
                          margin: '14px 0 6px',
                        }}>
                          <span style={{
                            padding: '3px 10px',
                            borderRadius: 999,
                            fontSize: 10, fontWeight: 700,
                            color: 'var(--text-secondary)',
                            background: 'rgba(255,255,255,0.72)',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          }}>
                            {timeBreakLabel}
                          </span>
                        </div>
                      )}
                      <div
                        className={`flex gap-2 ${isMe ? 'flex-row-reverse' : ''}`}
                        style={{ marginTop: sameSenderAsPrev ? 2 : 8 }}>
                      {/* Avatar only on last message of cluster — for others only */}
                      {!isMe && (
                        sameSenderAsNext
                          ? <div style={{ width: 28, flexShrink: 0 }} />
                          : <div style={{ flexShrink: 0, alignSelf: 'flex-end' }}>
                              <Avatar avatarUrl={msg.senderAvatar || null} name={msg.senderName} size={28} />
                            </div>
                      )}

                      <div
                        style={{
                          maxWidth: '78%',
                          display: 'flex', flexDirection: 'column',
                          alignItems: isMe ? 'flex-end' : 'flex-start',
                        }}>
                        {/* Sender name — only first of cluster, for others */}
                        {!isMe && !sameSenderAsPrev && (
                          <span className="text-xs font-semibold mb-0.5"
                            style={{ color: 'var(--text-secondary)', marginLeft: 12 }}>
                            {msg.senderName}
                          </span>
                        )}

                        {/* Reply preview — compact quote above the bubble */}
                        {repliedTo && (
                          <div
                            style={{
                              fontSize: 11, padding: '5px 10px',
                              borderLeft: `3px solid ${isMe ? 'var(--ice)' : 'var(--border)'}`,
                              background: 'var(--surface-2)',
                              borderRadius: 8,
                              marginBottom: 3,
                              maxWidth: 260,
                              opacity: 0.85,
                            }}>
                            <div className="font-semibold" style={{ color: 'var(--text-secondary)', fontSize: 10, marginBottom: 1 }}>
                              ↪ {repliedTo.senderId === user?.id ? 'You' : repliedTo.senderName}
                            </div>
                            <div style={{
                              color: 'var(--text-secondary)',
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {repliedTo.content}
                            </div>
                          </div>
                        )}

                        {/* The bubble */}
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMsgId(isActive ? null : msg.id);
                          }}
                          style={{
                            position: 'relative',
                            padding: '9px 14px',
                            borderRadius: bubbleRadius,
                            background: isMe
                              // Own messages: solid ice-blue gradient. Other
                              // users: opaque white-ish surface that reads
                              // clearly against the patterned backdrop without
                              // needing a visible border. Removing the border
                              // and adding a soft drop-shadow gives a cleaner
                              // WhatsApp/iMessage feel than the old bordered
                              // chip look.
                              ? 'linear-gradient(135deg, rgba(0,212,255,0.96), rgba(0,180,220,0.92))'
                              : 'var(--surface)',
                            border: isMe
                              ? '1px solid rgba(0,212,255,0.4)'
                              : 'none',
                            color: isMe ? '#001a2a' : 'var(--text)',
                            fontSize: 14.5, lineHeight: 1.4,
                            fontWeight: isMe ? 500 : 400,
                            wordBreak: 'break-word',
                            cursor: 'pointer',
                            boxShadow: isMe
                              ? '0 2px 6px rgba(0,180,220,0.25)'
                              : '0 1px 2px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.03)',
                            transition: 'transform 0.15s',
                            transform: isActive ? 'scale(1.01)' : 'none',
                          }}>
                          {msg.isPinned && (
                            <div style={{
                              position: 'absolute', top: -6,
                              [isMe ? 'left' : 'right']: -4,
                              width: 18, height: 18, borderRadius: '50%',
                              background: 'var(--puck)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                            }}>
                              <Pin size={10} color="#fff" strokeWidth={2.5} />
                            </div>
                          )}
                          {renderMessageContent(msg.content)}
                        </div>

                        {/* Link preview card */}
                        {linkUrl && (
                          <a
                            href={linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '8px 12px', marginTop: 4,
                              background: 'var(--surface-2)', border: '1px solid var(--border)',
                              borderRadius: 10, fontSize: 12, color: 'var(--text-secondary)',
                              maxWidth: '100%', overflow: 'hidden',
                              textDecoration: 'none',
                            }}>
                            <ExternalLink size={12} style={{ flexShrink: 0, color: 'var(--ice)' }} />
                            <span style={{
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap', minWidth: 0,
                            }}>
                              {hostFromUrl(linkUrl)}
                            </span>
                          </a>
                        )}

                        {/* Apr 2026 — chat attachments. Each message
                            can carry one or more attachments. We
                            render images inline (auth-fetched as
                            blobs and turned into object URLs to
                            keep the Bearer token out of <img src>),
                            and PDFs as a download chip with file
                            name + size. */}
                        {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              marginTop: msg.content ? 8 : 0,
                              display: 'flex', flexDirection: 'column', gap: 6,
                            }}>
                            {msg.attachments.map(att => (
                              <ChatAttachment
                                key={att.id}
                                teamId={teamId}
                                attachment={att}
                                isMe={isMe}
                              />
                            ))}
                          </div>
                        )}

                        {msg.reactions && msg.reactions.length > 0 && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              display: 'flex', flexWrap: 'wrap', gap: 3,
                              marginTop: 4,
                              flexDirection: isMe ? 'row-reverse' : 'row',
                            }}>
                            {msg.reactions.map(r => {
                              const meReacted = r.userIds.includes(user?.id);
                              return (
                                <button
                                  key={r.emoji}
                                  onClick={() => toggleReaction(msg, r.emoji)}
                                  title={`${r.userIds.length} reaction${r.userIds.length !== 1 ? 's' : ''}`}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 3,
                                    padding: '2px 8px',
                                    borderRadius: 12,
                                    background: meReacted ? 'var(--ice-a15)' : 'var(--surface-2)',
                                    border: `1px solid ${meReacted ? 'var(--ice-a30)' : 'var(--border)'}`,
                                    color: meReacted ? 'var(--ice)' : 'var(--text-secondary)',
                                    fontSize: 12, lineHeight: 1.2,
                                    cursor: 'pointer', fontWeight: meReacted ? 700 : 500,
                                    transition: 'all 0.15s',
                                  }}>
                                  <span style={{ fontSize: 13 }}>{r.emoji}</span>
                                  <span style={{ fontSize: 10 }}>{r.userIds.length}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Emoji picker — shown when user taps the smile button in actions */}
                        {reactionPickerFor === msg.id && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              display: 'flex', gap: 2, marginTop: 4,
                              padding: '6px 8px', borderRadius: 16,
                              background: 'var(--surface)',
                              border: '1px solid var(--border)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                              flexDirection: isMe ? 'row-reverse' : 'row',
                            }}>
                            {REACTION_EMOJI.map(e => (
                              <button
                                key={e}
                                onClick={() => toggleReaction(msg, e)}
                                style={{
                                  width: 30, height: 30, borderRadius: '50%',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  background: 'transparent', border: 'none',
                                  cursor: 'pointer', fontSize: 18, lineHeight: 1,
                                  transition: 'transform 0.1s',
                                }}
                                onMouseEnter={ev => ev.currentTarget.style.transform = 'scale(1.25)'}
                                onMouseLeave={ev => ev.currentTarget.style.transform = 'none'}>
                                {e}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Actions row — shown when the bubble is tapped */}
                        {isActive && (
                          <div
                            style={{
                              display: 'flex', gap: 4,
                              marginTop: 4,
                              alignItems: 'center',
                              flexDirection: isMe ? 'row-reverse' : 'row',
                              // Mobile fix — this row lives inside the 78%-maxWidth
                              // bubble column, so on a ~390px phone a coach's own
                              // message (timestamp + React + Reply + Pin + Delete,
                              // all four buttons) can get tight. flexWrap lets it
                              // drop to a second line instead of clipping/overflowing
                              // rather than forcing a horizontal scrollbar.
                              flexWrap: 'wrap',
                            }}
                            onClick={(e) => e.stopPropagation()}>
                            <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginLeft: 6, marginRight: 6 }}>
                              {format(new Date(msg.createdAt), 'h:mm a')}
                            </span>
                            <button
                              onClick={() => {
                                setReactionPickerFor(reactionPickerFor === msg.id ? null : msg.id);
                                setActiveMsgId(null);
                              }}
                              title="React"
                              style={{
                                display: 'flex', alignItems: 'center',
                                padding: '3px 6px', borderRadius: 6,
                                background: 'var(--surface-2)', border: '1px solid var(--border)',
                                color: 'var(--text-secondary)', cursor: 'pointer',
                              }}>
                              <Smile size={11} />
                            </button>
                            <button
                              onClick={() => startReply(msg)}
                              title="Reply"
                              style={{
                                display: 'flex', alignItems: 'center', gap: 3,
                                padding: '3px 8px', borderRadius: 6,
                                background: 'var(--surface-2)', border: '1px solid var(--border)',
                                color: 'var(--text-secondary)', cursor: 'pointer',
                                fontSize: 10, fontWeight: 600,
                              }}>
                              <Reply size={10} /> Reply
                            </button>
                            {isCoach && (
                              <button
                                onClick={() => togglePin(msg.id)}
                                title={msg.isPinned ? 'Unpin' : 'Pin'}
                                style={{
                                  display: 'flex', alignItems: 'center',
                                  padding: '3px 6px', borderRadius: 6,
                                  background: msg.isPinned ? 'rgba(255,149,0,0.15)' : 'var(--surface-2)',
                                  border: `1px solid ${msg.isPinned ? 'rgba(255,149,0,0.35)' : 'var(--border)'}`,
                                  color: msg.isPinned ? 'var(--puck)' : 'var(--text-secondary)',
                                  cursor: 'pointer',
                                }}>
                                <Pin size={10} />
                              </button>
                            )}
                            {canAct && (
                              <button
                                onClick={() => deleteMsg(msg.id)}
                                title="Delete"
                                style={{
                                  display: 'flex', alignItems: 'center',
                                  padding: '3px 6px', borderRadius: 6,
                                  background: 'rgba(255,59,59,0.08)',
                                  border: '1px solid rgba(255,59,59,0.25)',
                                  color: 'var(--penalty)', cursor: 'pointer',
                                }}>
                                <Trash2 size={10} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    </div>
                  );
                })}
              </div>
            ))}
            {/* Typing indicator */}
            {typingUsers.length > 0 && (
              <div className="flex items-center gap-2 mt-3 ml-2 text-xs text-secondary">
                <div
                  className="flex items-center gap-1"
                  style={{
                    padding: '7px 12px', borderRadius: 14,
                    background: 'var(--surface-2)', border: '1px solid var(--border)',
                  }}>
                  {[0,1,2].map(i => (
                    <div key={i}
                      className="rounded-full animate-bounce"
                      style={{
                        width: 5, height: 5,
                        background: 'var(--text-secondary)',
                        animationDelay: `${i * 0.15}s`,
                      }} />
                  ))}
                </div>
                <span>{typingUsers.map(u => u.userName?.split(' ')[0]).join(', ')}</span>
              </div>
            )}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {/* Reply-to preview — shown above input when user is replying */}
      {replyTo && (
        <div
          className="flex-shrink-0 flex items-start gap-2 px-3 py-2 rounded-xl mb-2"
          style={{
            background: 'var(--surface-2)',
            borderLeft: '3px solid var(--ice)',
            border: '1px solid var(--border)',
          }}>
          <Reply size={13} style={{ color: 'var(--ice)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="text-xs font-semibold" style={{ color: 'var(--ice)' }}>
              Replying to {replyTo.senderId === user?.id ? 'yourself' : replyTo.senderName}
            </div>
            <div className="text-xs text-secondary truncate" style={{ fontSize: 11 }}>
              {replyTo.content}
            </div>
            {/* v2.5.185 — make it unmistakable that this reply stays
                private when answering a coaches-only message. */}
            {replyTo.recipientScope === 'coaches' && (
              <div className="text-xs font-semibold mt-1" style={{ color: '#a78bfa', fontSize: 10.5 }}>
                🎯 This was sent to coaches only — your reply stays coaches-only.
              </div>
            )}
          </div>
          <button
            onClick={() => {
              setReplyTo(null);
              // v2.5.186 — if the coaches-only scope was inherited from
              // the message being replied to, clear it on cancel so the
              // next ordinary message isn't silently kept private. (A
              // player who deliberately picked coaches-only via their
              // toggle re-picks it anyway; this only undoes the
              // reply-driven inheritance.)
              if (replyTo?.recipientScope === 'coaches') setPlayerRecipientScope('everyone');
            }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-secondary)', padding: 2, flexShrink: 0,
            }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Announcement banner */}
      {isAnnouncement && (
        <div
          className="flex-shrink-0 mb-2 px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-2"
          style={{
            background: 'rgba(255,149,0,0.1)',
            border: '1px solid rgba(255,149,0,0.25)',
            color: 'var(--puck)',
          }}>
          <Megaphone size={13} />
          <strong>Announcement mode</strong> — this message will email every team member and show a banner in chat.
        </div>
      )}

      {/* Apr 2026 — staged-file preview. Shown above the composer
          when the user has picked or dropped a file but hasn't sent
          it yet. Lets them confirm what they're about to send and
          gives a one-tap clear option. */}
      {pendingFile && (
        <div className="flex-shrink-0 mb-2 px-3 py-2 rounded-lg flex items-center gap-3"
          style={{
            background: 'var(--surface-2)', border: '1px solid var(--border)',
          }}>
          {pendingFile.type.startsWith('image/') ? (
            <img
              src={pendingFilePreviewUrl}
              alt={pendingFile.name}
              style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <div style={{
              width: 40, height: 40, borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--surface-3)', color: 'var(--ice)', flexShrink: 0,
            }}>
              <FileText size={20} />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13, fontWeight: 600, color: 'var(--text)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {pendingFile.name}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
              {(pendingFile.size / 1024 < 1024
                ? `${(pendingFile.size / 1024).toFixed(0)} KB`
                : `${(pendingFile.size / 1024 / 1024).toFixed(1)} MB`)}
              {' · '}
              {pendingFile.type.split('/')[1].toUpperCase()}
              {input.trim() ? ' · with caption' : ' · add a caption (optional)'}
            </div>
          </div>
          <button type="button"
            onClick={() => setPendingFile(null)}
            disabled={uploading}
            title="Remove file"
            style={{
              background: 'transparent', border: 'none',
              color: 'var(--text-2)', cursor: uploading ? 'not-allowed' : 'pointer',
              padding: 6, borderRadius: 6, flexShrink: 0,
            }}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* Hidden file input. Triggered by the + button in the
          composer. Restricted to whitelisted MIME types via accept
          (browser hint only — backend validates again). */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
        onChange={handleFilePick}
        style={{ display: 'none' }}
      />

      {/* Drag-drop overlay — covers the chat area when the user is
          hovering with a file. Subtle blue tint so it's clearly a
          "drop here" target without dominating the UI. */}
      {dragOver && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(0,212,255,0.10)',
          border: '4px dashed var(--ice)',
          pointerEvents: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24, fontWeight: 700, color: 'var(--ice)',
          fontFamily: 'var(--font-display)', letterSpacing: '0.06em',
        }}>
          📎 DROP TO ATTACH
        </div>
      )}

      {/* Input — rounded pill with auto-growing textarea, an emoji
          button, and a round send button. The wrapping <div> is
          positioned relative so the EmojiPicker can be absolutely
          anchored above it without escaping the form's column. */}
      {/* May 4 2026 (v2.5.70) — non-coach recipient scope picker.
          Two pill buttons above the composer: "👥 Entire team" (default)
          and "🎯 Coaches only". Coach feedback: "give two buttons for
          the player, either, the message goes to the coaches only, or
          to the entire team. By default, have the message go to the
          entire team." Coaches don't see this picker — they have the
          Announce button + the team-level chat_notify_mode for scope
          control. The picker is suppressed during file uploads
          (pendingFile) since the routing applies to email
          notifications and an upload caption follows the same path
          as a regular text message anyway — but the file path is a
          separate endpoint that doesn't yet honor recipientScope, so
          showing the picker would be misleading. ── */}
      {/* May 6 2026 (v2.5.73) — audience-awareness banner.
          Coach feedback: "let the user know who the current audience
          is. For example, right now, [from a coaches view with
          chat_notify_mode='coaches' on], let the coach know his chat
          will only be between coaches (if there are more than one
          coach, sent a note that he or she are the only coach). When
          announcement is turned on, then let the coach know the
          message will be announced, it's a one way announcements."

          The banner derives the audience description from chatConfig
          (chatNotifyMode, coachCount, memberCount) + current local
          state (isAnnouncement for coaches, playerRecipientScope for
          players). Renders in a small pill above the picker / form.

          NOT shown when a file is staged — the file caption follows
          the same routing as a text message anyway, but adding a
          banner there would crowd the small remaining real estate. ── */}
      {!pendingFile && chatConfig.memberCount !== undefined && (() => {
        // Compute audience description. Coach + Announce wins
        // priority, then coach normal mode, then player picker.
        let icon = '📬';
        let line = '';
        let tone = 'neutral'; // 'neutral' | 'warn' | 'announce'

        if (isCoach && isAnnouncement) {
          icon = '📣';
          tone = 'announce';
          line = `Announcement — every team member will be emailed and pushed. (Replies still appear in chat.)`;
        } else if (isCoach && (replyTo?.recipientScope === 'coaches' || playerRecipientScope === 'coaches')) {
          // v2.5.186 — coach is replying to a coaches-only message (or has
          // explicitly chosen coaches-only). The reply inherits that
          // private scope (see sendMessage), so the banner MUST say so —
          // previously it fell through to the team-default branch below
          // and wrongly told the coach "everyone will be emailed" while
          // the message actually went coaches-only. This is the visible
          // half of the privacy-leak fix: the coach now sees the reply
          // is staying private.
          const others = Math.max(0, (chatConfig.coachCount || 0) - 1);
          icon = '🎯';
          line = others > 0
            ? `Replying privately — only coaches and managers will see this (${others} other ${others === 1 ? 'recipient' : 'recipients'}).`
            : `Replying privately — coaches-only. You're the only coach, so no one else is notified.`;
        } else if (isCoach) {
          // Coach using regular chat — team-level chat_notify_mode applies.
          const mode = chatConfig.chatNotifyMode || 'everyone';
          const cc   = chatConfig.coachCount  || 0;
          const mc   = chatConfig.memberCount || 0;
          if (mode === 'off') {
            icon = '🔕';
            tone = 'warn';
            line = `Email notifications are off for chat — no one will be emailed. Use Announce to email everyone.`;
          } else if (mode === 'coaches') {
            // Subtract self from "other coaches" — the coach knows
            // they sent the message; what they care about is who
            // ELSE gets the email.
            const others = Math.max(0, cc - 1);
            if (others === 0) {
              icon = '📭';
              tone = 'warn';
              line = `Coaches-only mode is on, but you're the only coach — no one will be emailed.`;
            } else {
              icon = '🎯';
              line = `Only coaches and managers will be emailed (${others} ${others === 1 ? 'recipient' : 'recipients'}).`;
            }
          } else {
            // 'everyone' — most common
            const others = Math.max(0, mc - 1);
            line = others > 0
              ? `Everyone on the team will be emailed (${others} ${others === 1 ? 'recipient' : 'recipients'}).`
              : `You're the only team member — no one to email yet.`;
          }
        } else {
          // Non-coach player. The picker chooses the scope.
          const cc = chatConfig.coachCount  || 0;
          const mc = chatConfig.memberCount || 0;
          if (playerRecipientScope === 'coaches') {
            icon = '🎯';
            line = cc > 0
              ? `Only coaches and managers will be emailed (${cc} ${cc === 1 ? 'recipient' : 'recipients'}).`
              : `No coaches found on this team — your message will still appear in chat.`;
          } else {
            const others = Math.max(0, mc - 1);
            line = others > 0
              ? `Everyone on the team will be emailed (${others} ${others === 1 ? 'recipient' : 'recipients'}).`
              : `You're the only team member — no one to email yet.`;
          }
        }

        const palette = {
          neutral:  { bg: 'rgba(0,212,255,0.06)', border: 'rgba(0,212,255,0.18)', text: 'var(--text-2)' },
          warn:     { bg: 'rgba(255,149,0,0.06)', border: 'rgba(255,149,0,0.20)', text: 'var(--puck)' },
          announce: { bg: 'rgba(167,139,250,0.08)', border: 'rgba(167,139,250,0.30)', text: '#a78bfa' },
        }[tone];

        return (
          <div className="flex items-center gap-2 mb-2 flex-shrink-0"
            style={{
              padding: '5px 10px', borderRadius: 8,
              background: palette.bg,
              border: `1px solid ${palette.border}`,
              color: palette.text,
              fontSize: 11.5, lineHeight: 1.4, fontWeight: 500,
            }}>
            <span style={{ fontSize: 13, lineHeight: 1, flexShrink: 0 }}>{icon}</span>
            <span style={{ flex: 1, minWidth: 0 }}>{line}</span>
          </div>
        );
      })()}

      {!isCoach && !pendingFile && (
        <div className="flex items-center gap-2 mb-2 flex-shrink-0" style={{ paddingTop: 2 }}>
          <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)', fontWeight: 600 }}>
            To:
          </span>
          {[
            { key: 'everyone', emoji: '👥', label: 'Entire team' },
            { key: 'coaches',  emoji: '🎯', label: 'Coaches only' },
          ].map(opt => {
            const active = playerRecipientScope === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setPlayerRecipientScope(opt.key)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold transition-all"
                style={{
                  background: active ? 'var(--ice-a12, rgba(0,212,255,0.12))' : 'var(--surface-2)',
                  border: `1px solid ${active ? 'var(--ice-a40, rgba(0,212,255,0.40))' : 'var(--border)'}`,
                  color: active ? 'var(--ice)' : 'var(--text-2)',
                  letterSpacing: '0.02em',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                }}
                title={opt.key === 'coaches'
                  ? 'Only coaches and managers will be emailed/pushed about this message. The message itself is still visible in the team chat — this only changes who gets notified.'
                  : 'Every team member will be emailed/pushed about this message.'}>
                <span style={{ fontSize: 13, lineHeight: 1 }}>{opt.emoji}</span>
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      <form
        onSubmit={sendMessage}
        className="flex items-end gap-2 flex-shrink-0"
        style={{ paddingTop: 2, position: 'relative' }}>
        {/* Apr 2026 — composer emoji picker. Renders above the input
            (anchorAbove=true) so it doesn't obscure the message
            history above and doesn't get cut off by the on-screen
            keyboard on mobile. Closes on outside click. */}
        {composerEmojiOpen && (
          <EmojiPicker
            onSelect={insertEmoji}
            onClose={() => setComposerEmojiOpen(false)}
            anchorAbove={true}
          />
        )}
        <div style={{
          flex: 1,
          display: 'flex', alignItems: 'center',
          background: 'var(--surface-2)',
          border: `1px solid ${isAnnouncement ? 'rgba(255,149,0,0.35)' : 'var(--border)'}`,
          borderRadius: 20,
          padding: '4px 6px 4px 14px',
          transition: 'border-color 0.15s',
        }}>
          {/* Apr 30 2026 (App_Change_List #33) — file attachment
              trigger. Renamed from paperclip to "+" per the change
              list spec, matching iMessage / Discord / Slack-newer
              UX conventions. Visually it's a circular button with a
              Plus icon — affordance is "this opens a menu / adds
              something" which is more discoverable than a paperclip
              for users who don't immediately think "files."
              Sits to the LEFT of the textarea (in the rounded pill)
              so it's reachable with the same thumb that types.
              Disabled while a file is already staged or while a send
              is in flight. Hidden entirely when the platform admin
              has the feature off. */}
          {chatConfig.attachmentsEnabled && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!!pendingFile || uploading || sending}
              title="Attach a file (image or PDF)"
              aria-label="Attach a file"
              style={{
                background: pendingFile ? 'var(--ice-a12)' : 'var(--surface-2)',
                border: `1px solid ${pendingFile ? 'var(--ice-a30)' : 'var(--border)'}`,
                padding: 0,
                width: 30, height: 30,
                borderRadius: '50%',
                cursor: pendingFile || uploading ? 'not-allowed' : 'pointer',
                color: pendingFile ? 'var(--ice)' : 'var(--text-2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                opacity: pendingFile || uploading ? 0.5 : 1,
                transition: 'color 0.15s, opacity 0.15s, background 0.15s, border-color 0.15s',
              }}>
              <Plus size={18} strokeWidth={2.4} />
            </button>
          )}
          <textarea
            ref={textareaRef}
            enterKeyHint="send"
            value={input}
            onChange={handleInput}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage(e);
              }
            }}
            placeholder={isAnnouncement ? 'Write an announcement…'
              : (pendingFile ? 'Add a caption (optional) — tap send'
                : (replyTo ? `Reply to ${replyTo.senderName?.split(' ')[0]}…` : 'Message'))}
            rows={1}
            maxLength={2000}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none', outline: 'none',
              color: 'var(--text)',
              // iOS Safari auto-zooms into any text input with font-size < 16px
              // when it receives focus — an unfixable WebKit behavior. 16px is
              // the magic threshold; at 16 or above, focus does NOT trigger
              // the zoom. Using 16px here keeps the input readable and
              // prevents the whole chat from rescaling when the user taps to
              // type. Line-height 1.35 compensates so the pill doesn't feel
              // taller than before.
              fontSize: 16, lineHeight: 1.35,
              fontFamily: 'inherit',
              resize: 'none',
              // May 6 2026 (v2.5.72) — coach feedback: "move the
              // message cursor a bit more to the right from the plus
              // sign to add a file." The textarea was butting right
              // up against the Plus button (no gap, padding-left 0)
              // which made the placeholder/cursor look cramped. 10px
              // of left padding gives the input room to breathe
              // without pushing the right-side controls (emoji + send)
              // around. Right padding stays 0 — the emoji and send
              // buttons after the textarea handle their own spacing.
              padding: '6px 0 6px 10px',
              minHeight: 20, maxHeight: 120,
            }}
          />
          {/* Emoji picker trigger — sits inside the rounded pill on
              the right edge, just before the send button. */}
          <button
            type="button"
            onClick={() => setComposerEmojiOpen(o => !o)}
            title="Add emoji"
            aria-label="Add emoji"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 6,
              borderRadius: '50%',
              cursor: 'pointer',
              color: composerEmojiOpen ? 'var(--ice)' : 'var(--text-2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              transition: 'color 0.15s',
            }}>
            <Smile size={18} strokeWidth={2} />
          </button>
        </div>
        <button
          type="submit"
          disabled={(!input.trim() && !pendingFile) || sending || uploading}
          style={{
            width: 38, height: 38, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: (input.trim() || pendingFile) && !sending && !uploading
              ? (isAnnouncement ? 'var(--puck)' : 'var(--ice)')
              : 'var(--surface-3)',
            border: 'none',
            color: (input.trim() || pendingFile) && !sending && !uploading ? '#000' : 'var(--text-secondary)',
            cursor: (input.trim() || pendingFile) && !sending && !uploading ? 'pointer' : 'not-allowed',
            flexShrink: 0,
            transition: 'all 0.15s',
            boxShadow: (input.trim() || pendingFile) && !sending && !uploading ? '0 2px 8px rgba(0,212,255,0.25)' : 'none',
          }}>
          {(sending || uploading)
            ? <div className="spinner" style={{ width: 15, height: 15 }} />
            : <Send size={16} strokeWidth={2.2} />}
        </button>
      </form>
      {input.length > 1800 && (
        <div className="text-xs text-secondary text-right mt-1" style={{ fontSize: 10 }}>
          {input.length}/2000
        </div>
      )}
    </div>
  );
}

// ─── ChatAttachment (Apr 2026) ────────────────────────────────────────
// Renders a single attached file inside a chat bubble. Two modes:
//   • image — fetched as a blob (so the Authorization header is
//     applied) then turned into an object URL for an <img>. Tap
//     opens a fullscreen viewer (a separate state-managed overlay
//     in ChatPage; for now, opening in a new tab is a tolerable
//     fallback).
//   • file (PDFs, etc.) — rendered as a download chip showing
//     filename + size. Tap opens the file in a new tab.
//
// Why blob + object URL instead of direct <img src=API_URL>:
// the API requires a Bearer token. Putting the token in the URL
// (as a query param) would leak it into browser history and any
// referer headers a parent frame might forward. Fetching with
// `api` adds the header, then URL.createObjectURL gives us a
// self-contained URL that doesn't need auth.
//
// Why this lives at module scope (not inside ChatPage): keeps
// ChatPage's component logic readable, and makes it easier to
// memoize per-message renders later.
function ChatAttachment({ teamId, attachment, isMe }) {
  const isImage = attachment.mimeType?.startsWith('image/');
  const isPdf   = attachment.mimeType === 'application/pdf';
  const [objectUrl, setObjectUrl]   = useState(null);
  const [loadError, setLoadError]   = useState(false);

  // Lazy-fetch the image bytes. PDFs aren't pre-fetched (could be
  // big and we don't render them inline); the chip's onClick
  // fetches on demand and pops a new tab.
  useEffect(() => {
    if (!isImage) return;
    let cancelled = false;
    let url = null;
    (async () => {
      try {
        const { data } = await api.get(
          `/teams/${teamId}/chat/attachments/${attachment.id}`,
          { responseType: 'blob' }
        );
        if (cancelled) return;
        url = URL.createObjectURL(data);
        setObjectUrl(url);
      } catch (err) {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
    // We intentionally do NOT include `api` (stable singleton) — only
    // the data inputs that would change the resource being fetched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, attachment.id, isImage]);

  // For PDFs (and any future non-image type), fetch on click and
  // open the resulting object URL in a new tab. Keeps the auth
  // token out of the URL while still giving a one-click
  // "download/view" experience.
  const openFileInNewTab = async () => {
    try {
      const { data } = await api.get(
        `/teams/${teamId}/chat/attachments/${attachment.id}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(data);
      // Open in a new tab. Revoke after a delay so the new tab has
      // time to load before the URL is invalidated.
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error('Failed to open attachment');
    }
  };

  const sizeStr = attachment.sizeBytes < 1024 * 1024
    ? `${(attachment.sizeBytes / 1024).toFixed(0)} KB`
    : `${(attachment.sizeBytes / 1024 / 1024).toFixed(1)} MB`;

  if (isImage) {
    if (loadError) {
      return (
        <div style={{
          padding: '8px 10px', borderRadius: 8,
          background: 'var(--surface-2)', color: 'var(--text-2)',
          fontSize: 12, fontStyle: 'italic',
        }}>
          (Image failed to load — {attachment.filename})
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={openFileInNewTab}
        title={`${attachment.filename} (${sizeStr}) — tap to open full size`}
        style={{
          background: 'transparent',
          border: 'none', padding: 0,
          borderRadius: 10, overflow: 'hidden',
          cursor: 'pointer',
          maxWidth: '100%',
        }}>
        {objectUrl ? (
          <img
            src={objectUrl}
            alt={attachment.filename}
            style={{
              maxWidth: 240, maxHeight: 240,
              display: 'block',
              borderRadius: 10,
            }}
          />
        ) : (
          <div style={{
            width: 200, height: 140,
            background: 'var(--surface-2)',
            borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-2)', fontSize: 11,
          }}>
            Loading image…
          </div>
        )}
      </button>
    );
  }

  // PDF / generic file chip.
  return (
    <button
      type="button"
      onClick={openFileInNewTab}
      title={`Open ${attachment.filename}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px',
        background: isMe ? 'rgba(255,255,255,0.18)' : 'var(--surface-2)',
        border: `1px solid ${isMe ? 'rgba(255,255,255,0.25)' : 'var(--border)'}`,
        borderRadius: 10,
        textAlign: 'left',
        cursor: 'pointer',
        maxWidth: 280,
      }}>
      <div style={{
        width: 32, height: 32, borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isPdf ? 'rgba(255,68,68,0.15)' : 'var(--surface-3)',
        color: isPdf ? 'var(--penalty)' : 'var(--ice)',
        flexShrink: 0,
      }}>
        <FileText size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600,
          color: isMe ? '#001a2a' : 'var(--text)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {attachment.filename}
        </div>
        <div style={{
          fontSize: 11,
          color: isMe ? 'rgba(0,26,42,0.7)' : 'var(--text-2)',
        }}>
          {(attachment.mimeType || 'file').replace('application/', '').toUpperCase()} · {sizeStr}
        </div>
      </div>
      <Download size={14} style={{
        flexShrink: 0,
        color: isMe ? '#001a2a' : 'var(--text-2)',
      }} />
    </button>
  );
}
